import { useMemo } from 'react'
import { useTranslation } from '@disco/i18n/use-translation'
import * as yup from 'yup'

type TranslateFn = ReturnType<typeof useTranslation>['t']

interface SignupInput {
  type: string
  name: string
  label: string
  placeholder?: string
  required?: boolean
  fields?: SignupInput[]
}

const buildEmailSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:emailAddressIsRequired', 'Email address is required'))
    .email(t('accounts:enterAValidEmailAddress', 'Enter a valid email address'))
    .trim()

const buildFullNameSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:fullNameIsRequired', 'Full Name is required'))
    .min(
      2,
      t(
        'accounts:fullNameMustBeAtLeast2Characters',
        'Full name must be at least 2 characters',
      ),
    )
    .trim()

const buildBusinessNameSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:businessNameIsRequired', 'Business Name is required'))
    .min(1, t('accounts:businessNameIsRequired', 'Business Name is required'))
    .trim()

const buildSubdomainSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:discoUrlIsRequired', 'DISCO URL is required'))
    .min(1, t('accounts:discoUrlIsRequired', 'DISCO URL is required'))
    .trim()

const buildUserTypeSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      t('accounts:pleaseSelectAValidOption', 'Please select a valid option'),
    )
    .oneOf(
      ['artist', 'business'],
      t('accounts:pleaseSelectAValidOption', 'Please select a valid option'),
    )

const buildVerificationHandleSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      t(
        'accounts:verificationHandleIsRequired',
        'Verification handle is required',
      ),
    )
    .min(
      1,
      t(
        'accounts:verificationHandleIsRequired',
        'Verification handle is required',
      ),
    )
    .trim()

const buildTermsAcceptanceSchema = (t: TranslateFn) =>
  yup
    .boolean()
    .oneOf(
      [true],
      t(
        'accounts:youMustAgreeToTheTermsAndPrivacy',
        'You must agree to the Terms of Service and Privacy Policy',
      ),
    )

const buildFileSchema = (t: TranslateFn) =>
  yup
    .mixed()
    .required(t('accounts:fileIsRequired', 'File is required'))
    .test(
      'fileSize',
      t(
        'accounts:fileSizeMustBeLessThan5mb',
        'File size must be less than 5MB',
      ),
      value => {
        if (!value) return true
        const file = value as File
        return file.size <= 5 * 1024 * 1024
      },
    )
    .test(
      'fileType',
      t('accounts:onlyImageFilesAreAllowed', 'Only image files are allowed'),
      value => {
        if (!value) return true
        const file = value as File
        return file.type.startsWith('image/')
      },
    )

// `{{field}} is required` for dynamically-labelled fields.
const requiredField = (t: TranslateFn, field: string) =>
  t('accounts:fieldIsRequired', '{{field}} is required', { field })

const buildEmailStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    email: buildEmailSchema(t),
  })

const buildNameStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    fullName: buildFullNameSchema(t),
    userType: buildUserTypeSchema(t),
  })

const buildArtistProjectStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    business_name: yup
      .string()
      .required(t('accounts:projectNameIsRequired', 'Project Name is required'))
      .min(1, t('accounts:projectNameIsRequired', 'Project Name is required'))
      .trim(),
    subdomain: buildSubdomainSchema(t),
  })

const buildArtistRoleStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    user_role: yup
      .string()
      .required(
        requiredField(
          t,
          t(
            'accounts:whatBestDescribesYourRole',
            'What best describes your role?',
          ),
        ),
      )
      .trim(),
  })

const buildArtistVerificationStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    verification_source: yup
      .string()
      .required(
        t(
          'accounts:verificationSourceIsRequired',
          'Verification source is required',
        ),
      )
      .trim(),
    verification_handle: buildVerificationHandleSchema(t),
    termsAcceptance: buildTermsAcceptanceSchema(t),
    marketing_optin: yup.boolean().optional(),
  })

const buildBusinessProjectStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    business_name: buildBusinessNameSchema(t),
    subdomain: buildSubdomainSchema(t),
    logo: yup.mixed().optional(),
  })

const buildBusinessDetailsStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    businessType: yup
      .string()
      .required(
        requiredField(
          t,
          t(
            'accounts:whatBestDescribesYourBusiness',
            'What best describes your business?',
          ),
        ),
      )
      .trim(),
    teamSize: yup
      .string()
      .required(
        requiredField(t, t('accounts:sizeOfYourTeam', 'Size of your team')),
      )
      .trim(),
    catalogSize: yup
      .string()
      .required(requiredField(t, t('accounts:catalogSize', 'Catalog size')))
      .trim(),
  })

const buildBusinessVerificationStepSchema = (t: TranslateFn) =>
  yup.object().shape({
    verification_source_1: yup
      .string()
      .required(
        t(
          'accounts:verificationSourceIsRequired',
          'Verification source is required',
        ),
      )
      .trim(),
    verification_handle_1: buildVerificationHandleSchema(t),
    verification_source_2: yup
      .string()
      .required(
        t(
          'accounts:verificationSourceIsRequired',
          'Verification source is required',
        ),
      )
      .trim(),
    verification_handle_2: buildVerificationHandleSchema(t),
    termsAcceptance: buildTermsAcceptanceSchema(t),
    marketing_optin: yup.boolean().optional(),
  })

const buildStepValidationSchema = (inputs: SignupInput[], t: TranslateFn) => {
  const schemaFields: Record<string, yup.AnySchema> = {}

  inputs.forEach(input => {
    if (input.required) {
      if (input.type === 'verification_pair') {
        input.fields?.forEach((field: SignupInput) => {
          if (field.required) {
            if (field.name.includes('verification_handle')) {
              schemaFields[field.name] = buildVerificationHandleSchema(t)
            } else {
              schemaFields[field.name] = yup
                .string()
                .required(requiredField(t, field.placeholder || field.name))
                .trim()
            }
          }
        })
      } else {
        // Handle different input types
        if (input.type === 'checkbox') {
          schemaFields[input.name] = yup
            .boolean()
            .oneOf([true], requiredField(t, input.label))
        } else if (input.type === 'file') {
          schemaFields[input.name] = buildFileSchema(t)
        } else {
          let fieldSchema: yup.AnySchema = yup
            .string()
            .required(requiredField(t, input.label))
            .trim()
          if (input.name === 'email') {
            fieldSchema = buildEmailSchema(t)
          } else if (input.name === 'fullName') {
            fieldSchema = buildFullNameSchema(t)
          } else if (input.name === 'business_name') {
            fieldSchema = buildBusinessNameSchema(t)
          } else if (input.name === 'subdomain') {
            fieldSchema = buildSubdomainSchema(t)
          } else if (input.name === 'userType') {
            fieldSchema = buildUserTypeSchema(t)
          } else if (input.name === 'user_role') {
            // Only required when a businessType is selected in the same step
            fieldSchema = yup.string().when('businessType', {
              is: (val: unknown) => typeof val === 'string' && val.length > 0,
              then: schema =>
                (schema as yup.StringSchema<string>)
                  .required(requiredField(t, input.label))
                  .trim(),
              otherwise: schema =>
                (schema as yup.StringSchema<string | undefined>)
                  .optional()
                  .trim(),
            })
          }

          schemaFields[input.name] = fieldSchema
        }
      }
    }
  })

  return yup.object().shape(schemaFields)
}

export const useValidationSchemas = () => {
  const { t } = useTranslation('accounts')

  return useMemo(
    () => ({
      emailStepSchema: buildEmailStepSchema(t),
      nameStepSchema: buildNameStepSchema(t),
      artistProjectStepSchema: buildArtistProjectStepSchema(t),
      artistRoleStepSchema: buildArtistRoleStepSchema(t),
      artistVerificationStepSchema: buildArtistVerificationStepSchema(t),
      businessProjectStepSchema: buildBusinessProjectStepSchema(t),
      businessDetailsStepSchema: buildBusinessDetailsStepSchema(t),
      businessVerificationStepSchema: buildBusinessVerificationStepSchema(t),
      createStepValidationSchema: (inputs: SignupInput[]) =>
        buildStepValidationSchema(inputs, t),
    }),
    [t],
  )
}
